<script setup lang="ts">
import DataTableV2, { type TableColumn, type TableAction } from '@uiKit/components/DataTableV2.vue'
import StatusBadge from '@uiKit/components/StatusBadge.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'

import { formatKey } from '@shared/utils'
import { cryptoWalletStatusColor } from '../constants/cryptoWalletStatus'
import type { WalletManagementRow } from '../types/cryptoWalletTypes'

interface Props {
  rows: WalletManagementRow[]
  loading: boolean
  total: number
  page: number
  pageSize: number
  search: string
  actions: TableAction<WalletManagementRow>[]
}

defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:search', value: string): void
  (e: 'update:page', value: number): void
  (e: 'select', row: WalletManagementRow): void
}>()

const COLUMNS: TableColumn[] = [
  { key: 'investorName', label: 'INVESTOR', minWidth: '200px' },
  { key: 'walletAddress', label: 'CUSTODY WALLET', minWidth: '220px' },
  // Named to match the VAISP onboarding queue — same column, same underlying
  // `wallet_status`, so it must not go by two names across two screens.
  { key: 'walletStatus', label: 'VALIDATION STATUS', width: '160px' },
  { key: 'trustlines', label: 'TRUSTLINES', width: '190px' },
  // The column the screen exists for: a non-zero count is an order the ledger
  // is holding until a trustline is opened.
  { key: 'awaitingOptIn', label: 'AWAITING OPT-IN', width: '160px' },
]
</script>

<template>
  <DataTableV2
    :columns="COLUMNS"
    :rows="rows"
    :loading="loading"
    :total="total"
    :page="page"
    :page-size="pageSize"
    :search="search"
    :local-search="false"
    :local-pagination="false"
    :actions="actions"
    row-key="investorId"
    title="Wallet management"
    subtitle="Custody wallets and the token trustlines each investor's orders depend on"
    search-placeholder="Search by investor, email, or wallet address"
    empty-title="No investors found"
    empty-subtitle="No investor matches the current filters."
    density="comfortable"
    @update:search="emit('update:search', $event)"
    @update:page="emit('update:page', $event)"
    @row-click="(row) => emit('select', row as WalletManagementRow)"
  >
    <template #cell-investorName="{ row }">
      <Box flex direction="col">
        <Text type="body-sm" weight="medium">
          {{ (row as WalletManagementRow).investorName || '—' }}
        </Text>
        <Text type="caption" color="muted">{{ (row as WalletManagementRow).email }}</Text>
      </Box>
    </template>

    <template #cell-walletAddress="{ row }">
      <Text v-if="(row as WalletManagementRow).walletAddress" type="body-sm" family="mono" truncate>
        {{ (row as WalletManagementRow).walletAddress }}
      </Text>
      <Text v-else type="body-sm" color="muted">Not created</Text>
    </template>

    <template #cell-walletStatus="{ row }">
      <StatusBadge
        v-if="(row as WalletManagementRow).walletStatus"
        :color="cryptoWalletStatusColor((row as WalletManagementRow).walletStatus ?? undefined)"
        :label="formatKey((row as WalletManagementRow).walletStatus ?? '')"
      />
      <Text v-else type="body-sm" color="muted">—</Text>
    </template>

    <template #cell-trustlines="{ row }">
      <Box flex gap="2" align="center">
        <StatusBadge
          color="success"
          hide-dot
          :label="`${(row as WalletManagementRow).activeTrustlines} active`"
        />
        <StatusBadge
          v-if="(row as WalletManagementRow).pendingTrustlines > 0"
          color="warning"
          hide-dot
          :label="`${(row as WalletManagementRow).pendingTrustlines} pending`"
        />
        <StatusBadge
          v-if="(row as WalletManagementRow).failedTrustlines > 0"
          color="danger"
          hide-dot
          :label="`${(row as WalletManagementRow).failedTrustlines} failed`"
        />
      </Box>
    </template>

    <template #cell-awaitingOptIn="{ row }">
      <StatusBadge
        v-if="(row as WalletManagementRow).awaitingOptInOrders > 0"
        color="warning"
        :label="`${(row as WalletManagementRow).awaitingOptInOrders} order(s)`"
      />
      <Text v-else type="body-sm" color="muted">None</Text>
    </template>
  </DataTableV2>
</template>
